// core
import React, { useState } from "react";
import cx from "classnames";
import InsurerLogo from "../../../../../common/components/InsurerLogo/InsurerLogo";
import HeaderBreakdown from "../../Components/HeaderBreakdown";
import HeaderCashback from "../../Components/HeaderCashback";
import QuoteCallToAction from "../../Components/QuoteCallToAction/QuoteCallToAction";
import Premium from "../../../Premium/Premium";
import Icon from "../../../../../common/components/Icon/Icon";
import { getProductTypeAsString } from "../../../../../common/shared/enumHelpers";
import { ClientProductTypesList } from "../../../../../common/interfaces/enums/ProductType";
import IQuoteCardResult from "../../../../interfaces/results/IQuoteCardResult";
import { IClient } from "../../../../../common/interfaces/IClient";
import { QuoteTableCallToActionFunction } from "../../../../hooks/useQuoteSections";
import { PaymentFrequency } from "../../../../../common/interfaces/enums/PaymentFrequency";
import IQuoteTableState from "../../../../types/quoteTable/IQuoteTableState";
import QuoteResultError from "../../../Modals/QuoteResultErrorModal/QuoteResultError";

// interfaces
interface IQuoteTableHeaderContentProps {
    bannerHeadingText: string;
    card: IQuoteCardResult;
    clients: IClient[];
    handleCallToAction: QuoteTableCallToActionFunction;
    handlePaymentFrequencyChange: (paymentFreq: PaymentFrequency) => Promise<void>;
    applicationURL: string;
    quoteState: IQuoteTableState;
    groupId: string;
}

// rendering
const QuoteTableHeaderContent: React.FC<IQuoteTableHeaderContentProps> = (props) => {
    const {
        bannerHeadingText,
        card,
        clients,
        handlePaymentFrequencyChange,
        quoteState,
        applicationURL,
        groupId
    } = props;
    const {
        totalCashback,
        result: { providerId, totalPremium, paymentFrequency, errors }
    } = card;

    const { hasBlockingError, isOverMinimumPremium, isDisabled } = quoteState;

    const [isOpen, setIsOpen] = useState(false);

    const productList = card.result.settings.map((s) => s.productType);

    return (
        <div data-provider-id={providerId}>
            <div
                className={cx([
                    "layout-column-banner",
                    "panel-best-choice",
                    "text-banner",
                    { "text-banner-hidden": !bannerHeadingText }
                ])}>
                {bannerHeadingText}
            </div>

            <Premium
                totalPremium={totalPremium}
                size="extra-large"
                paymentFrequency={paymentFrequency}
                isBelowMinimumPremium={!isOverMinimumPremium}
                onPaymentFrequencyChange={handlePaymentFrequencyChange}
                hasBlockingError={hasBlockingError}
                className="gap-m"
            />

            {!hasBlockingError && (
                <React.Fragment>
                    <HeaderBreakdown
                        card={card}
                        clients={clients}
                        hasDiscountApplied={card.result.benefits.some(
                            (b) => b.discounts.length > 0
                        )}
                        className="gap-m"
                    />

                    <HeaderCashback totalCashback={totalCashback} />
                </React.Fragment>
            )}

            <InsurerLogo
                includesAboutInsurerModal
                providerId={providerId}
                className={cx(["layout-insurer-logo"])}
            />

            {/* Call to action */}

            {!hasBlockingError && (
                    <QuoteCallToAction
                        groupId={groupId}
                        card={card}
                        isDisabled={
                            hasBlockingError ||
                            !quoteState.isOverMinimumPremium ||
                            quoteState.isDisabled
                        }
                        url={applicationURL}
                        className="btn-small"
                        premium={totalPremium}
                    />
                )}

            {!hasBlockingError && (
                <div className={cx("gap-ml", "gap-pre-xl")}>
                    {ClientProductTypesList.map((product, index) => {
                        if (!productList.includes(product)) return null;

                        return (
                            <span
                                className={cx(
                                    "text-font-size-m",
                                    "text-primary",
                                    "text-semi-bold",
                                    "gap-m",
                                    "layout-flex-row--centered"
                                )}
                                key={index}>
                                <Icon size="m" id="SaveTick" className={cx("space-ml")} />
                                {getProductTypeAsString(product, true)} cover
                            </span>
                        );
                    })}
                </div>
            )}
            {hasBlockingError && card.result.errors.length > 0 && (
                <React.Fragment>
                    <QuoteResultError
                        errors={card.result.errors}
                        isOpen={isOpen}
                        onClose={() => setIsOpen(false)}
                    />
                    <span>
                        Insurer not available.
                        <a onClick={() => setIsOpen(true)}> Find out why.</a>
                    </span>
                </React.Fragment>
            )}
        </div>
    );
};

export default React.memo(QuoteTableHeaderContent);
